import { baseApi } from 'shared/api/baseApi'
import { CMS_DOCUMENT_CATEGORIES_TAG } from 'shared/api/tags'
import type {
    CMSCreateDocumentCategoryRequest,
    CMSCreateDocumentRequest,
    CMSDeleteDocumentCategoryRequest,
    CMSDeleteDocumentRequest,
    CMSDocument,
    CMSDocumentCategory,
    CMSDocumentCategoryListParams,
    CMSGenerateUploadUrlResponse,
    CMSUpdateDocumentCategoryRequest,
    CMSUpdateDocumentRequest,
} from '../model/types'

export const cmsDocumentsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getCmsDocumentCategories: build.query<
            CMSDocumentCategory[],
            CMSDocumentCategoryListParams
        >({
            query: ({ organizationId }) => ({
                url: `organizations/${organizationId}/cms-document-categories`,
                method: 'GET',
            }),
            providesTags: [CMS_DOCUMENT_CATEGORIES_TAG],
        }),

        getCmsDocumentCategory: build.query<
            CMSDocumentCategory,
            { organizationId: string; categoryId: string }
        >({
            query: ({ organizationId, categoryId }) => ({
                url: `organizations/${organizationId}/cms-document-categories/${categoryId}`,
                method: 'GET',
            }),
            providesTags: [CMS_DOCUMENT_CATEGORIES_TAG],
        }),

        createCmsDocumentCategory: build.mutation<
            CMSDocumentCategory,
            CMSCreateDocumentCategoryRequest
        >({
            query: ({ organizationId, ...body }) => ({
                url: `organizations/${organizationId}/cms-document-categories`,
                method: 'POST',
                body,
            }),
            invalidatesTags: [CMS_DOCUMENT_CATEGORIES_TAG],
        }),

        updateCmsDocumentCategory: build.mutation<
            CMSDocumentCategory,
            CMSUpdateDocumentCategoryRequest
        >({
            // Category update is a full-body PUT (not PATCH) — the PATCH route
            // does not exist (404); PUT requires the complete payload.
            query: ({ organizationId, categoryId, data }) => ({
                url: `organizations/${organizationId}/cms-document-categories/${categoryId}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: [CMS_DOCUMENT_CATEGORIES_TAG],
        }),

        deleteCmsDocumentCategory: build.mutation<
            void,
            CMSDeleteDocumentCategoryRequest
        >({
            // The backend only exposes a single-resource DELETE
            // (`/cms-document-categories/{id}`); there is no bulk route.
            // Callers deleting several categories must issue one call per id.
            query: ({ organizationId, categoryId }) => ({
                url: `organizations/${organizationId}/cms-document-categories/${categoryId}`,
                method: 'DELETE',
            }),
            invalidatesTags: [CMS_DOCUMENT_CATEGORIES_TAG],
        }),

        createCmsDocument: build.mutation<
            CMSDocument,
            CMSCreateDocumentRequest
        >({
            query: ({ organizationId, categoryId, ...body }) => ({
                url: `organizations/${organizationId}/cms-document-categories/${categoryId}/documents`,
                method: 'POST',
                body,
            }),
            invalidatesTags: [CMS_DOCUMENT_CATEGORIES_TAG],
        }),

        updateCmsDocument: build.mutation<
            CMSDocument,
            CMSUpdateDocumentRequest
        >({
            // Document update is a full-body PUT (not PATCH) — the backend
            // requires the complete document payload, partial bodies 422.
            query: ({ organizationId, categoryId, documentId, data }) => ({
                url: `organizations/${organizationId}/cms-document-categories/${categoryId}/documents/${documentId}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: [CMS_DOCUMENT_CATEGORIES_TAG],
        }),

        deleteCmsDocument: build.mutation<void, CMSDeleteDocumentRequest>({
            query: ({ organizationId, categoryId, documentId }) => ({
                url: `organizations/${organizationId}/cms-document-categories/${categoryId}/documents/${documentId}`,
                method: 'DELETE',
            }),
            invalidatesTags: [CMS_DOCUMENT_CATEGORIES_TAG],
        }),

        generateCmsDocumentUploadUrl: build.mutation<
            CMSGenerateUploadUrlResponse,
            { organizationId: string; categoryId: string }
        >({
            query: ({ organizationId, categoryId }) => ({
                url: `organizations/${organizationId}/cms-document-categories/${categoryId}/documents/generate-upload-url`,
                method: 'POST',
            }),
        }),
    }),
})

export const {
    useGetCmsDocumentCategoriesQuery,
    useGetCmsDocumentCategoryQuery,
    useCreateCmsDocumentCategoryMutation,
    useUpdateCmsDocumentCategoryMutation,
    useDeleteCmsDocumentCategoryMutation,
    useCreateCmsDocumentMutation,
    useUpdateCmsDocumentMutation,
    useDeleteCmsDocumentMutation,
    useGenerateCmsDocumentUploadUrlMutation,
} = cmsDocumentsApi
